import { Observable, Subject } from "rxjs";
import { orderTypes } from "../../core/shared/contentTypes";
import Utils from "../../core/shared/utils";
import { ISOTService } from "../services";
import { SOTProductExtraItemViewModel, SOTProductExtraGroupViewModel, SOTProductViewModel } from "./product.model";
import { IBenefitBasketData } from "./bonusSystemSOT.facade";

// ViewModels
export const MAX_BASKETITEM_COUNT = 99;
export const MAX_BASKETITEM_EXTRA_COUNT = 99;

/** sot ui warenkorb */
export class SOTBasketViewModel {

  private _products: SOTBasketProductViewModel[] = [];
  public get products(): SOTBasketProductViewModel[] {
    return this._products;
  }

  private _basketChanged: Subject<void> = new Subject();

  public get BasketChanged(): Observable<void> {
    return this._basketChanged.asObservable();
  }

  private _sotService: ISOTService;
  public constructor(sotService: ISOTService) {
    this._sotService = sotService;
  }

  /** Steuersätze und der Betrag aller Artikel im warenkorb */
  public getVats(): SOTBasketVatsViewModel {
    const vats = new SOTBasketVatsViewModel();
    this.products.forEach((product) => {
      vats.merge(product.getVats());
    });
    // round
    vats.vats.forEach((vat) => {
      vat.amount = Utils.moneyRound(vat.amount);
      vat.vat = Utils.moneyRound(vat.vat);
    });
    return vats;
  }

  /** Summe des Warenkorbes mit Rabatt für die UI.
   * Es wird bei bedarf an das Menü Konzept angepasst.
   */
  public getTotalSumForView(): number {
    const me = this;
    if (me._sotService.isMenuConceptViewActive()) {
      const sum = Utils.moneyRound(me.products.reduce((sum, product) => sum += product.menuPriceWithDepositSum, 0));
      return sum;
    } else {
      return me.getTotalSum();
    }
  }

  /** Summe des Warenkorbes mit Rabatt */
  public getTotalSum(): number {
    return Utils.moneyRound(this.products.reduce((sum, product) => sum += product.totalSum, 0));
  }

  /** Summe des Warenkorbes ohne Rabatt */
  public getTotalSumWithoutDiscount(): number {
    return Utils.moneyRound(this.products.reduce((sum, product) => sum += product.totalSumWithoutDiscount, 0));
  }

  /** Summe des Pfands im Warenkorb */
  public getDepositSum(): number {
    return Utils.moneyRound(this.products.reduce((sum, product) => sum += product.depositSum, 0));
  }

  /** Anzahl der Artikel des Warenkorbes */
  public getCount(): number {
    return this.products.reduce((count, product) => count += product.count, 0);
  }

  /** push or unshift product to basket, if item exists it wil be incremented */
  public addProduct(product: SOTProductViewModel, to_top: boolean = false): void {
    this.addBasketProduct(new SOTBasketProductViewModel(product), to_top)
  }

  /** push or unshift basket product to basket, if item exists it wil be incremented */
  public addBasketProduct(product: SOTBasketProductViewModel, to_top: boolean = false): void {
    const me = this;
    // search new product in basket
    const basketitem = me.products.find((item) => item.isEqualTo(product));
    if (basketitem) {
      // if found only update the count
      basketitem.count = basketitem.count + product.count;
    } else {
      if (to_top) {
        me._products.unshift(product);
      } else {
        me._products.push(product);
      }
    }
    // filter empty products
    me._products = me._products.filter((product) => product.count > 0)
  }

  /** remove basket product from basket
   * ! Attention, it must be the product from basket self
   */
  public removeBasketProduct(product: SOTBasketProductViewModel): void {
    const me = this;
    // search new product in basket
    const index = me.products.indexOf(product);
    if (index > -1) {
      me.products.splice(index, 1);
      if (product.redemption_id) {
        me._sotService.SOTConnectionSendReleaseCoupon(product.redemption_id);
      }
      me._basketChanged.next();
    } else {
      console.debug("Basket removeProduct: product not found")
    }
  }

  /** Notify subscribers of a basket change (e.g. after a direct count update). */
  public notifyBasketChanged(): void {
    this._basketChanged.next();
  }

  /** fügt den "artikel" warenkorb in den momentanen warenkorb
   * achtung cleart den "artikel" warenkorb, damit er wieder verwendet werden
   * kann und nicht versehendlich erneut gemergt wird
   */
  public merge(other: SOTBasketViewModel): void {
    const me = this;
    other.products.forEach(product => {
      me.addBasketProduct(product);
    });
    me._basketChanged.next();
    other.clear();
  }

  /** ersetzt den artikel im warenkorb, wird zum bearbeiten des Warenkorbs verwendet
   * benutze getProductForEdit um ein artikel zu bekommen, welcher direkt bearbeitet werden kann
   */
  public replaceProduct(old_product: SOTBasketProductViewModel, new_product: SOTBasketProductViewModel): void {
    const me = this;
    // search new product in basket
    const index = me.products.indexOf(old_product);
    if (index > -1) {
      me.products[index] = new_product;
    } else {
      console.debug("Basket replaceProduct: product not found")
    }
  }

  /** wird zum bearbeiten des Warenkorbs verwendet, daraus muss die oberfläche vorbesetzt werden
   * danach kann replaceProduct
   */
  public getProductForEdit(product: SOTBasketProductViewModel): SOTBasketProductEditViewModel {
    const me = this;
    let result: SOTBasketProductEditViewModel = null;
    // search new product in basket
    const index = me.products.indexOf(product);
    if (index > -1) {
      result = new SOTBasketProductEditViewModel(me.products[index]);
    } else {
      console.debug("Basket replaceProduct: product not found")
    }
    return result;
  }

  /** bereinigt den inhalt des warenkorbes */
  public clear(): void {
    this._products = [];
  }

  /** erzeugt von sich selbst ein SOTBasketModel zum ertsellen einer Bestellung am POS */
  public cretaeCopyAsSOTBasketModel(paymenttype: PaymentType, cardno: string, pagerno: number): SOTBasketModel {
    const me = this;
    const sotBasket = new SOTBasketModel();
    const vat_level = (me._sotService.GetOrderType() == orderTypes.pickup ? VatLevel.pickup : VatLevel.inHouse);
    sotBasket.paymenttype = paymenttype;
    sotBasket.cardno = cardno;
    sotBasket.pagerno = pagerno;
    sotBasket.customer = 0;
    sotBasket.webvoucherno = ""; // wird im sot Service gefüllt
    sotBasket.tip = 0; // wird im sot Service nachberechnet
    sotBasket.price = me.getTotalSum();
    sotBasket.discount_percentage = me._sotService.GetBestDiscountPercent();
    sotBasket.products = [];
    me.products.forEach(product => {
      const sotProduct = new SOTBasketProductModel();
      sotProduct.product_id = product.model.product_id;
      sotProduct.pe = product.model.bestPE;
      sotProduct.price = Utils.moneyRound(product.price + product.deposit);
      sotProduct.vat_level = vat_level;
      sotProduct.count = product.count;
      // addons, sind rez_ids der abfrageb, die so oft auftauchen, wie sie gebucht wurden
      // bei einem extra mit subextra, müssen beide (extra und subextra) ids auftauchen
      const addons = new Array<number>();
      product.extra_groups.forEach(extra_group => {
        extra_group.extras.forEach(extra => {
          if (extra.sub_extras.length === 0) {
            for (let i = 0; i < extra.count; ++i) {
              addons.push(extra.rez_id);
            }
          } else {
            extra.sub_extras.forEach(sub_extra => {
              for (let i = 0; i < sub_extra.count; ++i) {
                addons.push(extra.rez_id);
                addons.push(sub_extra.rez_id);
              }
            });
          }
        });
      });
      sotProduct.addons = addons.join(",");
      // Benefit extras are already included; prevent double-charging. Pfand remains chargeable.
      const isBenefitItem = product.isBenefitItem(product.benefitLossId, product.bonusactionId, product.benefitId);
      const extrasPriceSum = product.extra_groups.reduce((price, group) => price += group.priceSum, 0);
      sotProduct.addons_price = isBenefitItem ? 0 : Utils.moneyRound(extrasPriceSum);
      sotProduct.valid_date = 0;
      sotProduct.bonusactionId = product.bonusactionId;
      sotProduct.benefitId = product.benefitId;
      sotProduct.benefitProductId = product.benefitProductId;
      sotProduct.benefitLossId = product.benefitLossId;
      sotProduct.benefitCostPoints = product.benefitCostPoints;
      // Copy one-time coupon fields only for 9-digit single-use coupon articles.
      if (product.isCouponItem) {
        sotProduct.coupon_id = product.coupon_id;
        sotProduct.coupon_code = product.coupon_code;
        sotProduct.redemption_id = product.redemption_id;
      }
      sotBasket.products.push(sotProduct);
    });
    sotBasket.partial = false; // wird im sot Service gefüllt
    sotBasket.remaining_amount = 0; // wird im sot Service gefüllt
    sotBasket.webvouchers = []; // wird im sot Service gefüllt
    return sotBasket;
  }
}

/** enthält die steuer */
export class SOTBasketVatsViewModel {
  /** Einzelne steuersätze */
  public vats: SOTBasketVatViewModel[] = [];

  /** fügt mwst hinzu, wenn es gleichen steuersatz hat wird es addiert */
  public addVat(percentage: number, amount: number): void {
    const me = this;
    const vat = (amount * percentage) / (100 + percentage)
    me._addVat(percentage, amount, vat);
  }

  /** (intern) fügt mwst hinzu, wenn es gleichen steuersatz hat wird es addiert */
  private _addVat(percentage: number, amount: number, vat: number): void {
    const me = this;
    const sameVat = me.vats.find(vat => vat.percentage === percentage);
    if (sameVat) {
      sameVat.amount += amount;
      sameVat.vat += vat;
    } else {
      const newVat = new SOTBasketVatViewModel();
      newVat.percentage = percentage;
      newVat.amount = amount;
      newVat.vat = vat;
      me.vats.push(newVat);
      // sort after add new vat
      me.vats.sort((l, r) => r.percentage - l.percentage); // desc
    }
  }

  /** führt die steursätze von zwei zusammen */
  public merge(other: SOTBasketVatsViewModel): SOTBasketVatsViewModel {
    const me = this;
    other.vats.forEach(other_vat => {
      me._addVat(other_vat.percentage, other_vat.amount, other_vat.vat);
    });
    return me;
  }
}

/** steuer */
export class SOTBasketVatViewModel {
  /** steuersatz */
  public percentage: number;
  /** steuer */
  public vat: number;
  /** betrag auf den die steuer berechnet wurde */
  public amount: number;
}

/** wird genutzt um ein Artikel im Warenkorb nachträglich zu bearbeiten */
export class SOTBasketProductEditViewModel {
  public product_id: number;
  public count: number;
  public extra_groups: SOTBasketProductExtraGroupEditViewModel[];

  private _view_model: SOTBasketProductViewModel;
  /** basis */
  public get view_model(): SOTBasketProductViewModel {
    return this._view_model;
  }

  constructor(view_model: SOTBasketProductViewModel) {
    const me = this;
    me._view_model = view_model;
    me.product_id = me._view_model.model.product_id;
    me.count = me._view_model.count;
    me.extra_groups = me._view_model.extra_groups.map((extra_group) => new SOTBasketProductExtraGroupEditViewModel(extra_group));
  }
}

/** Extras gruppe (Abfragen) zum berarbeiten */
export class SOTBasketProductExtraGroupEditViewModel {
  public query: number;
  public extras: SOTBasketProductExtraEditViewModel[];

  constructor(extra_group: SOTBasketItemExtraGroupViewModel) {
    const me = this;
    me.query = extra_group.query;
    me.extras = new Array<SOTBasketProductExtraEditViewModel>();
    extra_group.extras.forEach((extra) => {
      if (extra.sub_extras.length === 0) {
        const model = new SOTBasketProductExtraEditViewModel();
        model.count = extra.count;
        model.extra_id = extra.extra_id;
        model.sub_extra_id = 0;
        me.extras.push(model);
      } else {
        extra.sub_extras.forEach(sub_extra => {
          const model = new SOTBasketProductExtraEditViewModel();
          model.count = sub_extra.count;
          model.extra_id = extra.extra_id;
          model.sub_extra_id = sub_extra.extra_id;
          me.extras.push(model);
        });
      }
    });
  }
}

/** Extras eintrag zum bearbeiten (als vorlage) */
export class SOTBasketProductExtraEditViewModel {
  public count: number;
  public extra_id: number;
  public sub_extra_id: number;
}

/** representier ein artikel im warenkorb */
export class SOTBasketProductViewModel {
  /** bezeichnung des artikels */
  public get label(): string {
    return this._model.label;
  }

  /** image path — benefit image if benefit item, else product image */
  public get imagePath(): string {
    const me = this;
    return me._isBenefit ? me.benefitImageUrl : me._model.image_path;
  }

  /** whether item has an image */
  public get hasImage(): boolean {
    const me = this;
    return me._isBenefit ? !!me.benefitHasImage : me._model.has_image;
  }

  private _count: number = 1; // count of the product
  /** anzahl des artikels */
  public get count(): number {
    return this._count;
  }
  /** verändert die anzahl, ist durch 0 und MAX_BASKETITEM_COUNT begernzt */
  public set count(count: number) {
    this._count = Math.min(Math.max(count, 0), MAX_BASKETITEM_COUNT); // between limit min: 0 - max: MAX_BASKETITEM_COUNT
  }

  private _extra_groups: SOTBasketItemExtraGroupViewModel[] = []; // count of the product
  /** extra gruppen (abfragen) des artikels
   * ACHTUNG! array darf nicht verändert werden */
  public get extra_groups(): SOTBasketItemExtraGroupViewModel[] {
    return this._extra_groups;
  }

  /** basePrice with min force price of extras (without noChoise price) */
  public get menuPrice(): number {
    const me = this;
    // es muss das model durchlaufen werden nicht das basket item
    const mainProduct = me._model;
    const minForceExtraPrice = mainProduct.extraGroups.reduce<number>((sum, group) => sum += mainProduct.calcMenuMinForcePrice(group), 0)
    return me.basePrice + minForceExtraPrice;
  }

  /** menuPrice with deposit */
  public get menuPriceWithDepositSum(): number {
    // menuPrice with force deposit and goup menuPrice + menuDeposit without force deposit
    return this.menuPrice + // basis Preis + minmal Preis aller nicht durchzubuchenden Abfragen
      this._model.depositWithExtras +
      this._extra_groups.reduce<number>((sum, group) => sum += group.menuPriceSum + group.menuDepositSumWithoutMinForce, 0); // alle gebuchten Auswahlen
  }

  /** basePrice of the product */
  public get basePrice(): number {
    return this._isBenefit ? 0 : Utils.moneyRound(this._model.price);
  }

  /** basePrice with applied best discount limited by noDiscount and minDiscount and extras */
  public get price(): number {
    if (this._isBenefit) { return 0; }
    return this._model.discountPrice + this._extra_groups.reduce<number>((sum, group) => sum += group.priceSum, 0);
  }

  /** basePrice without discount and extras without discount*/
  public get priceWithoutDiscount(): number {
    if (this._isBenefit) { return 0; }
    return this._model.price + this._extra_groups.reduce<number>((sum, group) => sum += group.priceSumWithoutDiscount, 0);
  }
  /** price * count */
  public get priceSum(): number {
    return this.price * this._count;
  }
  /** gesamter pfand */
  public get deposit(): number {
    return this._model.deposit + this._extra_groups.reduce<number>((sum, group) => sum += group.depositSum, 0);
  }
  /** deposit * count */
  public get depositSum(): number {
    return this.deposit * this._count;
  }
  /** with deposit */
  public get has_deposit(): boolean {
    return this.deposit > 0;
  }
  /** priceSum + depositSum  */
  public get totalSum(): number {
    return this.priceSum + this.depositSum;
  }

  /** priceSumWithoutDiscount + depositSum  */
  public get totalSumWithoutDiscount(): number {
    return (this.priceWithoutDiscount * this._count) + this.depositSum;
  }

  /** Steuersätze und der Betrag */
  public getVats(): SOTBasketVatsViewModel {
    const me = this;
    const vats = new SOTBasketVatsViewModel();
    const prices = me._model.getPrices();
    vats.addVat(prices.vat, this.totalSum);
    // todo extras haben momentan keine vats
    //const base_price = (prices.discountPrice + prices.discount) * me.count;
    //vats.addVat(prices.vat, base_price);
    //me._extra_groups.reduce((vats, group) => vats.merge(group.getVats(prices)), vats);
    return vats;
  }

  /** gültigkeitsdatum (momentan nicht genutzt) */
  public valid_date: number; // entered date for a ticket

  private _model: SOTProductViewModel;
  /** basis */
  public get model(): SOTProductViewModel {
    return this._model;
  }
  /** Bonus action ID */
  public bonusactionId?: number;
  /** Benefit ID */
  public benefitId?: number;
  /** Benefit product ID */
  public benefitProductId?: number;
  /** Benefit loss ID */
  public benefitLossId?: number;
  /** Benefit cost points */
  public benefitCostPoints?: number;
  /** Benefit image URL */
  public benefitImageUrl?: string;
  /** Indicates if the benefit has an image */
  public benefitHasImage?: boolean;

  /** Coupon ID for one-time coupons (9-digit). Only set when this article was redeemed via a 9-digit one-time code. */
  public coupon_id?: string;
  /** 9-digit coupon code for one-time coupons. Only set when coupon_id is set. */
  public coupon_code?: string;
  /** Redemption reference from $20; the POS needs it for ConfirmRedemption. */
  public redemption_id?: string;

  /** Whether this article is a one-time coupon article (coupon_id set and not null/undefined/empty). */
  public get isCouponItem(): boolean {
    const me = this;
    return me.coupon_id !== undefined && me.coupon_id !== null && me.coupon_id !== "";
  }

  private get _isBenefit(): boolean {
    const me = this;
    return me.isBenefitItem(me.benefitLossId, me.bonusactionId, me.benefitId);
  }

  public constructor(model: SOTProductViewModel) {
    this._model = model;
  }

  /**
   * Create a basket product with benefit data
   * Static factory method to create a benefit product
   */
  public static createBenefitProduct(model: SOTProductViewModel, benefitData: IBenefitBasketData): SOTBasketProductViewModel {
    const product = new SOTBasketProductViewModel(model);
    product.benefitId = benefitData.benefitId;
    product.bonusactionId = benefitData.bonusactionId;
    product.benefitProductId = benefitData.benefitProductId;
    product.benefitLossId = benefitData.benefitLossId;
    product.benefitCostPoints = benefitData.benefitCostPoints;
    product.benefitImageUrl = benefitData.benefitImageUrl;
    product.benefitHasImage = benefitData.benefitHasImage;
    return product;
  }

  /** Whether the product is a benefit item */
  public isBenefitItem(benefitLossId: number, bonusactionId: number, benefitId: number): boolean {
    const hasLossId = benefitLossId !== undefined && benefitLossId !== null;
    const hasBonusActionId = bonusactionId !== undefined && bonusactionId > 0;
    const hasBenefit = benefitId !== undefined && benefitId > 0;
    return hasLossId && hasBonusActionId && hasBenefit;
  }

  /**
   * Fügt ein Extra der Abfrage Gruppe hinzu (legt die Gruppe an, wenn nicht angelegt)
   * @param query Abfrage (gruppen)nummer
   * @param extra_id Abfrage ID
   * @param sub_extra_id SubAbfrage ID (zwang, wen Abfrage nicht "single" ist)
   */
  public addExtraGroup(query: number, extra_id: number, sub_extra_id: number = 0): void {
    const me = this;
    // search existing extra_group
    const extragroup = me._extra_groups.find((group) => group.query == query);
    if (extragroup) {
      extragroup.addExtra(extra_id, sub_extra_id);
    } else {
      // add new
      const group = new SOTBasketItemExtraGroupViewModel(me, query, extra_id, sub_extra_id);
      if (group.extras.length > 0) {
        me._extra_groups.push(group);
        me._extra_groups.sort((l, r) => +r.model.hasNoChoice - +l.model.hasNoChoice || l.query - r.query);
      } else {
        console.error("SOTBasketProductViewModel addExtraGroup: fail add group")
      }
    }
  }

  /**
   * entfernt ein extra aus der Abfrage gruppe
   * @param query Abfrage (gruppen)nummer
   * @param extra_id Abfrage ID
   * @param sub_extra_id SubAbfrage ID (zwang, wen Abfrage nicht "single" ist)
   */
  public removeExtraGroup(query: number, extra_id: number, sub_extra_id: number = 0): void {
    const me = this;
    // search existing extra_group
    const extragroup = me._extra_groups.find((group) => group.query == query);
    if (extragroup) {
      extragroup.removeExtra(extra_id, sub_extra_id);
    } else {
      console.error("SOTBasketProductViewModel removeExtraGroup: extra not found")
    }
    // filter empty extras_groups
    me._extra_groups = me._extra_groups.filter((group) => group.extras.length > 0);
    me._extra_groups.sort((l, r) => +r.model.hasNoChoice - +l.model.hasNoChoice || l.query - r.query);
  }

  /** extras werden als string Array (zeilenweise) für die ausgabe an der oberfläche aufbereitet */
  public extrasToStringArray(): string[] {
    const me = this;
    const output = new Array<string>();
    me._extra_groups.forEach(extra_group => {
      extra_group.extras.forEach(extra => {
        output.push(...extra.toStringArray());
      });
    });
    return output;
  }

  /** prüfung ob es die gleiche Artikel zusammenstellung ist
   * benötigt um die artikel im warenkorb zusammenzufassen
   */
  public isEqualTo(other: SOTBasketProductViewModel): boolean {
    const me = this;
    // Coupon articles (9-digit one-time codes) are never merged — each has its own coupon_id identity.
    const isCouponItem = me.isCouponItem;
    const otherIsCouponItem = other.isCouponItem;
    if (isCouponItem || otherIsCouponItem) {
      return false;
    }
    const isBenefit = me.bonusactionId > 0 && me.benefitId > 0;
    const otherIsBenefit = other.bonusactionId > 0 && other.benefitId > 0;
    // product_id ist gleich
    let isEqual = me._model.product_id == other._model.product_id;
    // valid_date ist gleich
    isEqual = isEqual && me.valid_date == other.valid_date;
    // extra groups equal
    isEqual = isEqual && me.extra_groups.length == other.extra_groups.length;
    // benefit-Status muss gleich sein (beides Benefit oder beides nicht)
    isEqual = isEqual && isBenefit === otherIsBenefit;
    // bei beidseitigem Benefit zusätzlich: gleiche Action und gleicher Benefit
    if (isEqual && isBenefit) {
      isEqual = isEqual && me.bonusactionId === other.bonusactionId;
      isEqual = isEqual && me.benefitId === other.benefitId;
    }
    // prüfen der gruppen im detail
    if (isEqual) {
      for (let i = 0; isEqual && i < me.extra_groups.length; ++i) {
        // extra_group ist gleich
        isEqual = isEqual && me.extra_groups[i].isEqualTo(other.extra_groups[i]);
      }
    }
    return isEqual;
  }
}

/** extra group */
export class SOTBasketItemExtraGroupViewModel {
  /** Abfrage (gruppen)nummer */
  public get query(): number {
    return this._model.query;
  }

  /** min force deposit for extras in group (if with choice) */
  public get menuMinForceDeposit(): number {
    const me = this;
    return me._parent.model.calcMenuMinForceDeposit(me._model);
  }

  /** min force price for extras in group (if with choice) */
  public get menuMinForcePrice(): number {
    const me = this;
    return me._parent.model.calcMenuMinForcePrice(me._model);
  }

  /** min force price for extras as sum with the max_choices */
  public get menuMinForcePriceSum(): number {
    return this.menuMinForcePrice * this._model.max_choices;
  }

  /** hilfs funktion um den artikel preis einfacher zu berechnen */
  public get priceSum(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.priceSum, 0);
  }

  /** hilfs funktion um den artikel menü preis einfacher zu berechnen */
  public get menuPriceSum(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.menuPriceSum, 0);
  }

  /** hilfs funktion um den artikel preis einfacher zu berechnen, ohne rabatt */
  public get priceSumWithoutDiscount(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.priceSumWithoutDiscount, 0);
  }

  /** hilfs funktion um den artikel pfand einfacher zu berechnen */
  public get depositSum(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.depositSum, 0);
  }

  /** Menü Pfand der Warenkorb artikel, ohne MinForceDeposit */
  public get menuDepositSumWithoutMinForce(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.menuDepositSumWithoutMinForce, 0);
  }

  /** hilfs funktion um den artikel total preis einfacher zu berechnen */
  public get totalSum(): number {
    return this._extras.reduce<number>((sum, extra) => sum += extra.priceSum + extra.depositSum, 0);
  }

  private _extras: SOTBasketItemExtraItemViewModel[];
  /** extras des artikels
   * ACHTUNG! array darf nicht verändert werden */
  public get extras(): ISOTBasketItemExtraItemViewModel[] {
    return this._extras;
  }

  private _parent: SOTBasketProductViewModel;
  /** parent */
  public get parent(): SOTBasketProductViewModel {
    return this._parent;
  }

  private _model: SOTProductExtraGroupViewModel;
  /** basis */
  public get model(): SOTProductExtraGroupViewModel {
    return this._model;
  }

  constructor(parent: SOTBasketProductViewModel, query: number, extra_id: number, sub_extra_id: number) {
    const me = this;
    me._parent = parent;
    me._model = me._parent.model.extraGroups.find((group) => group.query == query);
    me._extras = [];
    const extra = new SOTBasketItemExtraItemViewModel(me, extra_id, sub_extra_id);
    if (extra.count > 0) {
      me._extras.push(extra);
    } else {
      console.error("SOTBasketItemExtraGroupViewModel constructor: fail add extra")
    }
  }

  /**
 * Fügt ein Extra der Abfrage gruppe hinzu
 * @param extra_id Abfrage ID
 * @param sub_extra_id SubAbfrage ID (zwang, wen Abfrage nicht "single" ist)
 */
  public addExtra(extra_id: number, sub_extra_id: number = 0): void {
    const me = this;
    // search existing extra
    const extraitem = me._extras.find((item) => item.extra_id == extra_id);
    if (extraitem) {
      if (sub_extra_id == 0) {
        extraitem.count = extraitem.count + 1; // increment
      } else {
        extraitem.addSubExtra(sub_extra_id);
      }
    } else {
      // add new
      const extra = new SOTBasketItemExtraItemViewModel(me, extra_id, sub_extra_id);
      if (extra.count > 0) {
        me._extras.push(extra);
        me._extras.sort((l, r) => l.model.order - r.model.order);
      } else {
        console.error("SOTBasketItemExtraGroupViewModel addExtra: fail add extra")
      }
    }
  }

  /**
   * entfernt ein extra aus der Abfrage gruppe
   * @param extra_id Abfrage ID
   * @param sub_extra_id SubAbfrage ID (zwang, wen Abfrage nicht "single" ist)
   */
  public removeExtra(extra_id: number, sub_extra_id: number = 0): void {
    const me = this;
    // search new product in basket
    const extraitem = me._extras.find((item) => item.extra_id == extra_id);
    if (extraitem) {
      if (sub_extra_id == 0) {
        extraitem.count = extraitem.count - 1; // decrement
      } else {
        extraitem.removeSubExtra(sub_extra_id);
      }
    } else {
      console.error("SOTBasketItemExtraGroupViewModel removeExtra: extra not found")
    }
    // filter empty extras
    me._extras = me._extras.filter((extra) => extra.count > 0);
    me._extras.sort((l, r) => l.model.order - r.model.order);
  }

  // todo extras haben momentan keine vats
  ///** berechnet die steuer */
  //public getVats(prices: ProductPricesModel): SOTBasketVatsViewModel {
  //  const me = this;
  //  const vats = new SOTBasketVatsViewModel();
  //  me._extras.reduce((vats, extra) => vats.merge(extra.getVats(prices)), vats)
  //  return vats;
  //}

  /** prüfung ob es die gleiche gruppen zusammenstellung ist
 * benötigt um die artikel im warenkorb zusammenzufassen
 */
  public isEqualTo(other: SOTBasketItemExtraGroupViewModel): boolean {
    const me = this;
    // query (gruppennummer) ist gleich
    let isEqual = me.query == other.query;
    // extras equal
    isEqual = isEqual && me.extras.length == other.extras.length;
    // prüfen der extras im detail
    if (isEqual) {
      for (let i = 0; isEqual && i < me.extras.length; ++i) {
        // extra ist gleich
        isEqual = isEqual && me._extras[i].isEqualTo(other._extras[i]);
      }
    }
    return isEqual;
  }
}

/** interface um nicht tiefer zu gehen und um felder zu versteken die zur internen bearbeitung verwendet werden */
export interface ISOTBasketItemSubExtraItemViewModel {
  readonly extra_id: number;
  readonly rez_id: number;
  readonly title: string;
  readonly priceSum: number;
  /** mit sub_extras ist es die anzahl der subextras */
  readonly count: number; // kein set, da zur internen nutzung
}

/** interface um felder zu versteken die zur internen bearbeitung verwendet werden */
export interface ISOTBasketItemExtraItemViewModel extends ISOTBasketItemSubExtraItemViewModel {
  readonly sub_extras: ISOTBasketItemSubExtraItemViewModel[];
  toStringArray(): string[];
}

/** extra (item der extra group) */
export class SOTBasketItemExtraItemViewModel implements ISOTBasketItemExtraItemViewModel {
  /** id of extra (aka extra_id) */
  public get extra_id(): number {
    return this._model.id;
  }

  /** rezeptur id für das POS */
  public get rez_id(): number {
    return this._model.rez_id;
  }

  /** bezeichnunng er abfrage (nicht die artikel bezeichnung) */
  public get title(): string {
    return this._model.title;
  }

  /** zur ausgabe an der oberfläche */
  public toStringArray(): string[] {
    const me = this;
    const output = new Array<string>();
    if (me._sub_extras.length === 0) {
      output.push((me._count > 1 ? me._count + "x " : "") + me.title);
    } else /* if (me._sub_extras.length > 0) */ {
      me._sub_extras.forEach(sub_extra => {
        output.push((sub_extra.count > 1 ? sub_extra.count + "x " : "") + me.title + " (" + sub_extra.title + ")");
      });
    }
    return output;
  }

  private _sub_extras: SOTBasketItemSubExtraItemViewModel[] = [];
  /** sub extras des extras
    * ACHTUNG! array darf nicht verändert werden */
  public get sub_extras(): ISOTBasketItemSubExtraItemViewModel[] {
    return this._sub_extras;

  }

  private _count: number = 0; // count of the extra
  /** anzahl des extras, bzw. die der subs */
  public get count(): number {
    const me = this;
    if (me._sub_extras.length == 0) {
      return me._count;
    } else {
      return me._sub_extras.reduce((count, sub) => count += sub.count, 0);
    }
  }

  /** verändert die anzahl, ist durch 0 und MAX_BASKETITEM_EXTRA_COUNT begernzt
   * zur internen nutzung, deswegen nicht im ISOTBasketItemExtraItemViewModel
   */
  public set count(count: number) {
    if (this._sub_extras.length == 0) {
      this._count = Math.min(Math.max(count, 0), MAX_BASKETITEM_EXTRA_COUNT); // between limit min: 0 - max: MAX_BASKETITEM_EXTRA_COUNT
    } else {
      console.error("SOTBasketItemExtraItemViewModel try set count with sub_extras")
    }
  }

  /** preis des extras (mit rabatt), bzw. die der subs */
  public get priceSum(): number {
    const me = this;
    const discountPrice = me._parent.parent.model.getExtraPrices(me.extra_id).discountPrice;
    if (me._sub_extras.length == 0) {
      return discountPrice * me._count;
    } else {
      return me._sub_extras.reduce((price, sub) => price += (discountPrice * sub.count) + sub.priceSum, 0);
    }
  }

  /** price as menu view */
  public get menuPriceSum(): number {
    const me = this;
    const discountPrice = me._parent.parent.model.getExtraPrices(me.extra_id).discountPrice - me._parent.menuMinForcePrice;
    if (me._sub_extras.length == 0) {
      return discountPrice * me._count;
    } else {
      return me._sub_extras.reduce((price, sub) => price += (discountPrice * sub.count) + sub.priceSum, 0);
    }
  }

  /** preis des extras (ohne rabatt), bzw. die der subs */
  public get priceSumWithoutDiscount(): number {
    const me = this;
    const basePrice = me._parent.parent.model.getExtraPrices(me.extra_id).price;
    if (me._sub_extras.length == 0) {
      return basePrice * me._count;
    } else {
      return me._sub_extras.reduce((price, sub) => price += (basePrice * sub.count) + sub.priceSumWithoutDiscount, 0);
    }
  }

  /** pfand des extras (mit rabatt), bzw. die der subs */
  public get depositSum(): number {
    const me = this;
    const baseDeposit = me._parent.parent.model.getExtraPrices(me.extra_id).deposit;
    if (me._sub_extras.length == 0) {
      return baseDeposit * me._count;
    } else {
      return me._sub_extras.reduce((deposit, sub) => deposit += (baseDeposit * sub.count) + sub.depositSum, 0);
    }
  }

  /** menü pfand des extras (mit rabatt), bzw. die der subs, ohne MinForceDeposit */
  public get menuDepositSumWithoutMinForce(): number {
    const me = this;
    const baseDepositWithoutMinForce = me._parent.parent.model.getExtraPrices(me.extra_id).deposit - me._parent.menuMinForceDeposit;
    if (me._sub_extras.length == 0) {
      return baseDepositWithoutMinForce * me._count;
    } else {
      return me._sub_extras.reduce((deposit, sub) => deposit += (baseDepositWithoutMinForce * sub.count) + sub.depositSum, 0);
    }
  }

  // todo extras haben momentan keine vats
  ///** berechnet die steuer */
  //public getVats(prices: ProductPricesModel): SOTBasketVatsViewModel {
  //  const me = this;
  //  const vats = new SOTBasketVatsViewModel();
  //  // hier die übergebenen prices verwenden um es zu beschläunigen und sie beim price und deposit nicht neu zu ermitteln
  //  if (me._sub_extras.length == 0) {
  //    const extra_prices = prices.extras[me.extra_id];
  //    const base_price = ((extra_prices.discountPrice + extra_prices.discount) * me.count);
  //    // todo extras benötigen vat und den vat mode
  //    console.error("todo mwst berechnung")
  //    vats.addVat(extra_prices.vat, base_price);
  //  } else {
  //    me._sub_extras.reduce((vats, sub) => vats.merge(sub.getVats(prices)), vats);
  //  }
  //  return vats;
  //}

  private _model: SOTProductExtraItemViewModel;
  /** basis */
  public get model(): SOTProductExtraItemViewModel {
    return this._model;
  }

  private _parent: SOTBasketItemExtraGroupViewModel;
  /** parent */
  public get parent(): SOTBasketItemExtraGroupViewModel {
    return this._parent;
  }

  constructor(parent: SOTBasketItemExtraGroupViewModel, extra_id: number, sub_extra_id: number) {
    const me = this;
    me._parent = parent;

    // find extra and set
    me._model = me._parent.model.extra_items.find((extra) => extra.id == extra_id);
    if (me._model) {
      // check sub_extra, is allowed
      if (me._model.without_subitem && sub_extra_id > 0) {
        console.error("SOTBasketItemExtraItemViewModel constructor: sub_extra_id setted for single")
      } else if ((!me._model.without_subitem) && sub_extra_id == 0) {
        console.error("SOTBasketItemExtraItemViewModel constructor: sub_extra_id not setted for not single")
      } else {
        // sub_extra_id is allowed
        if (sub_extra_id == 0) {
          me._count = 1; // extra erfolgreich gefunden
        } else {
          me.addSubExtra(sub_extra_id);
        }
      }
    } else {
      console.error("SOTBasketItemExtraItemViewModel constructor: extra for extra_id not found")
    }
  }

  /** add a subextra */
  public addSubExtra(sub_extra_id): void {
    const me = this;
    if (me._model.without_subitem) {
      console.error("SOTBasketItemExtraItemViewModel addSubExtra: try add sub_extra_id for single")
      return; // break
    }

    // search existing sub extra
    const sub = me._sub_extras.find((sub) => sub.extra_id == sub_extra_id);
    if (sub) {
      sub.count = sub.count + 1; // increment
    } else {
      // add new
      me._model.sub_items
      const sub_extra = new SOTBasketItemSubExtraItemViewModel(me, sub_extra_id);
      if (sub_extra.count > 0) {
        me._sub_extras.push(sub_extra);
        me._sub_extras.sort((l, r) => l.model.order - r.model.order);
      } else {
        console.error("SOTBasketItemExtraItemViewModel addSubExtra: fail add extra")
      }
    }
  }

  /** remove a subextra */
  public removeSubExtra(sub_extra_id: number): void {
    const me = this;
    // search existing sub extra
    const sub = me._sub_extras.find((sub) => sub.extra_id == sub_extra_id);
    if (sub) {
      sub.count = sub.count - 1; // decrement
    } else {
      console.error("SOTBasketItemExtraItemViewModel removeSubExtra: extra not found")
    }
    // filter empty extras
    me._sub_extras = me._sub_extras.filter((sub) => sub.count > 0);
    me._sub_extras.sort((l, r) => l.model.order - r.model.order);
  }

  /** prüfung ob es die gleiche extras zusammenstellung ist
 * benötigt um die artikel im warenkorb zusammenzufassen
 */
  public isEqualTo(other: SOTBasketItemExtraItemViewModel): boolean {
    const me = this;
    // extra_id ist gleich
    let isEqual = me.extra_id == other.extra_id;
    // sub_extras equal
    isEqual = isEqual && me._sub_extras.length == other._sub_extras.length;
    // anzahl ist gleich
    isEqual = isEqual && me.count == other.count;
    // prüfen der sub_extras im detail
    if (isEqual) {
      for (let i = 0; isEqual && i < me.sub_extras.length; ++i) {
        // sub_extra ist gleich
        isEqual = me._sub_extras[i].isEqualTo(other._sub_extras[i]);
      }
    }
    return isEqual;
  }
}

/** sub extra (item vom extra) */
export class SOTBasketItemSubExtraItemViewModel implements ISOTBasketItemSubExtraItemViewModel {
  /** id of extra (aka extra_id) */
  public get extra_id(): number {
    return this._model.id;
  }

  /** rezeptur id für das POS */
  public get rez_id(): number {
    return this._model.rez_id;
  }

  /** bezeichnunng er abfrage (nicht die artikel bezeichnung) */
  public get title(): string {
    return this._model.title;
  }

  private _count: number = 0; // count of the extra
  /** anzahl des extras, bzw. die der subs */
  public get count(): number {
    return this._count;
  }

  /** verändert die anzahl, ist durch 0 und MAX_BASKETITEM_EXTRA_COUNT begernzt
   * zur internen nutzung, deswegen nicht im ISOTBasketItemExtraItemViewModel
   */
  public set count(count: number) {
    this._count = Math.min(Math.max(count, 0), MAX_BASKETITEM_EXTRA_COUNT); // between limit min: 0 - max: MAX_BASKETITEM_EXTRA_COUNT
  }

  /** preis des extras (mit rabatt), bzw. die der subs */
  public get priceSum(): number {
    const me = this;
    return me._parent.parent.parent.model.getExtraPrices(me.extra_id).discountPrice * me._count;
  }

  /** preis des extras (ohne rabatt), bzw. die der subs */
  public get priceSumWithoutDiscount(): number {
    const me = this;
    return me._parent.parent.parent.model.getExtraPrices(me.extra_id).price * me._count;
  }

  /** pfand des extras, bzw. die der subs */
  public get depositSum(): number {
    const me = this;
    return me._parent.parent.parent.model.getExtraPrices(me.extra_id).deposit * me._count;
  }

  // todo extras haben momentan keine vats
  ///** berechnet die steuer */
  //public getVats(prices: ProductPricesModel): SOTBasketVatsViewModel {
  //  const me = this;
  //  const vats = new SOTBasketVatsViewModel();
  //  // hier die übergebenen prices verwenden um es zu beschläunigen und sie beim price und deposit nicht neu zu ermitteln
  //  const extra_prices = prices.extras[me.extra_id];
  //  const base_price = (extra_prices.discountPrice + extra_prices.discount * me.count);
  //  // todo extras benötigen vat und den vat mode
  //  console.error("todo momentan wird auf das extra die mwst des artikels angewant")
  //  vats.addVat(extra_prices.vat, base_price);
  //  return vats;
  //}

  private _model: SOTProductExtraItemViewModel;
  /** basis */
  public get model(): SOTProductExtraItemViewModel {
    return this._model;
  }

  private _parent: SOTBasketItemExtraItemViewModel;
  /** parent */
  public get parent(): SOTBasketItemExtraItemViewModel {
    return this._parent;
  }

  constructor(parent: SOTBasketItemExtraItemViewModel, extra_id: number) {
    const me = this;
    me._parent = parent;

    // find sub extra and set
    me._model = me._parent.model.sub_items.find((extra) => extra.id == extra_id);
    if (me._model) {
      // check is single
      if (!me._model.without_subitem) {
        console.error("SOTBasketItemSubExtraItemViewModel constructor: model is not single")
      } else {
        me._count = 1; // extra erfolgreich gefunden
      }
    } else {
      console.error("SOTBasketItemSubExtraItemViewModel constructor: sub_extra for extra_id not found")
    }
  }

  /** prüfung ob es die gleiche extras zusammenstellung ist
 * benötigt um die artikel im warenkorb zusammenzufassen
 */
  public isEqualTo(other: SOTBasketItemSubExtraItemViewModel): boolean {
    const me = this;
    // extra_id ist gleich
    let isEqual = me.extra_id == other.extra_id;
    // anzahl ist gleich
    isEqual = isEqual && me.count == other.count;
    return isEqual;
  }
}

export interface ProcessCard {
  no: string;
  amount: number;
  limit: number;
}

/** enum der Zahlungsart für das POS.
 * Achtung: die Zahlungsart darf nur einstellig sein, wegen des Parsers.
 */
export enum PaymentType {
  /** Zahlung erfolgt am ecTerminal */
  ecTerminal = 1,
  /** Zahlung erfolgt am POS */
  atPOS = 2,
  /** Zahlung erfolgt mit der Vorgangskarte */
  atCard = 3,
  /** Zahlung erfolgt vollständig über den Webgutschein */
  voucherPay = 4,
}

// SOT Model
export class SOTBasketModel {
  /** zahlungsart (1 = EC-Terminal, 2 = am POS, 3 = mit Vorgangskarte, 4 = mit Webgutschein) */
  public paymenttype: PaymentType;
  /** artikel */
  public products: SOTBasketProductModel[];
  /** vorgnagskartennummer */
  public cardno: string;
  /** pagernummer */
  public pagerno: number;
  /** account id (prepared for later) */
  public customer: number;
  /** webgutscheinnummer, wird für das Sammeln von Bonuspunkte genutzt */
  public webvoucherno: string;
  /** trinkgeld */
  public tip: number;
  /** angewandter rabattsatz  */
  public discount_percentage: number;
  /** gesamt preis (mit rabatt und trinkgeld) */
  public price: number;
  /** es handelt sich um eine Teilzahlung, rest erfolgt über webgutscheine */
  public partial: boolean;
  /** der noch zu zahlender Betrag */
  public remaining_amount: number;
  /** Webgutscheine die Verwendet wurden. Und in dieser reihenfolge auch belastet. */
  public webvouchers: SOTBasketVoucherModel[];
}

/** enum der Steuer für das POS */
export enum VatLevel {
  inHouse = 1,
  pickup = 2,
}

/** Artikel im Warenkorb */
export class SOTBasketProductModel {
  /** product id */
  public product_id: number;
  /** preisebene */
  public pe: number;
  /** 1 = im Haus, 2 = außer haus */
  public vat_level: VatLevel;
  /** quant/anzahl */
  public count: number;
  /** preis mit addons (mit rabatt) */
  public price: number;
  /** comma seperated addon_id (ArtikelExtra.RezID) list, like '4001,4003,4003' => 1x4001, 2x4003 */
  public addons: string;
  /** preis aller addons (mit rabatt) */
  public addons_price: number;
  /** gültigkeitsdatum des artikels, bei ticket einlösungs Tag */
  public valid_date: number;
  /** Bonus action ID */
  public bonusactionId?: number;
  /** Benefit ID */
  public benefitId?: number;
  /** Benefit product ID */
  public benefitProductId?: number;
  /** Benefit loss ID */
  public benefitLossId?: number;
  /** Benefit cost points */
  public benefitCostPoints?: number;
  /** Coupon ID for one-time coupons; only set for 9-digit one-time code articles. */
  public coupon_id?: string;
  /** 9-digit one-time coupon code; only set when coupon_id is present. */
  public coupon_code?: string;
  /** Redemption reference from $20; the POS needs it for ConfirmRedemption. */
  public redemption_id?: string;
}

/** Webgutschein aus dem Warenkorb */
export class SOTBasketVoucherModel {
  /** Gutscheinnummer */
  public no: string;
  /** Betrag, welcher für die Zahlung verwendet wird und eingelöst werden muss */
  public amount: number;
}